// Pure helpers (no imports), so `yarn test` can run them in plain Node

type Attribute = { trait_type: string, value: string }
type Counts = Record<string, Record<string, number>>

// /<40 hex dna> → dna (lowercase); anything else → undefined
export const dnaFromPath = (path: string) => path.match(/^\/([0-9a-f]{40})\/?$/i)?.[1].toLowerCase()

// Sort comparator: rank 1 (rarest) first, unknown ranks last
export const byRank = (rank: number[]) => (a: { number: number }, b: { number: number }) =>
	(rank[a.number] ?? Infinity) - (rank[b.number] ?? Infinity)

export const heat = [
	{ label: "Rare · under 1%", max: 1, color: "#FF8F7E" },
	{ label: "Uncommon · under 3%", max: 3, color: "#F4B860" },
	{ label: "Common", max: Infinity, color: "#4FA89C" },
]
export const heatOf = (pct: number) => heat.find(h => pct < h.max)!.color

// The ape's least common trait ("None" doesn't count as a trait to show off)
export const rarestTrait = (attributes: Attribute[], counts: Counts, total: number) => {
	const scored = attributes
		.filter(a => a.value !== "None" && counts[a.trait_type]?.[a.value])
		.map(a => ({ ...a, pct: counts[a.trait_type][a.value] / total * 100 }))
		.sort((a, b) => a.pct - b.pct)
	return scored[0]
}

// Marks background pixels (close to the top-left colour) reachable from the image edge, so same-coloured
// bits inside the ape are kept. RGBA in, 1 = background.
export const backgroundMask = (rgba: Uint8ClampedArray, w: number, h: number, tolerance = 24) => {
	const [r, g, b] = rgba
	const mask = new Uint8Array(w * h)
	const isBg = (i: number) => Math.abs(rgba[i * 4] - r) + Math.abs(rgba[i * 4 + 1] - g) + Math.abs(rgba[i * 4 + 2] - b) <= tolerance
	const stack: number[] = []
	for (let x = 0; x < w; x++) stack.push(x, (h - 1) * w + x)
	for (let y = 0; y < h; y++) stack.push(y * w, y * w + w - 1)
	while (stack.length) {
		const i = stack.pop()!
		if (mask[i] || !isBg(i)) continue
		mask[i] = 1
		const x = i % w
		if (x > 0) stack.push(i - 1)
		if (x < w - 1) stack.push(i + 1)
		if (i >= w) stack.push(i - w)
		if (i < w * (h - 1)) stack.push(i + w)
	}
	return mask
}

// Nicknames: 1–24 letters/numbers/spaces/basic punctuation, starting with a letter or number; "" clears it.
// No quotes or newlines, so the signed message below parses unambiguously.
export const NICKNAME_MAX = 24
export const isValidNickname = (s: string) => s === "" || /^[\p{L}\p{N}][\p{L}\p{N} .'!?&_-]{0,23}$/u.test(s)

// The exact text the holder signs with their wallet; the server rebuilds the request from it
export const nicknameMessage = (number: number, nickname: string, time: number) =>
	`Bored Ape Pastel Club\nSet the nickname of Bored Ape Pastel #${number} to "${nickname}"\nTime: ${time}`

export const parseNicknameMessage = (message: string) => {
	const m = message.match(/^Bored Ape Pastel Club\nSet the nickname of Bored Ape Pastel #(\d{1,4}) to "([^"\n]*)"\nTime: (\d{13})$/)
	if (!m || !isValidNickname(m[2])) return undefined
	return { number: Number(m[1]), nickname: m[2], time: Number(m[3]) }
}

// Helius enhanced transaction (only the fields we read)
export type HeliusTx = {
	signature: string
	timestamp: number
	tokenTransfers?: { fromUserAccount?: string, toUserAccount?: string, mint: string }[]
	events?: { nft?: { amount?: number, type?: string } }
}

// Owner changes of one NFT, newest first. Listings (which can park the NFT in an escrow) and transactions that don't
// move the mint are skipped; an empty `from` means it was minted. `lamports` is the mint or sale price; Helius also
// puts listing prices in the same field, so it only counts for those two types.
export const ownerChanges = (txs: HeliusTx[], mint: string) => txs
	.filter(tx => !tx.events?.nft?.type?.includes("LISTING"))
	.flatMap(tx => (tx.tokenTransfers ?? [])
		.filter(t => t.mint === mint && t.toUserAccount)
		.map(t => ({ signature: tx.signature, time: tx.timestamp, from: t.fromUserAccount || undefined, to: t.toUserAccount!, lamports: ["NFT_SALE", "NFT_MINT"].includes(tx.events?.nft?.type ?? "") ? tx.events?.nft?.amount : undefined })))
	.sort((a, b) => b.time - a.time)
